<template>
  <div class="columns is-mobile nav-brands">
    <div class="column brands-search">
      <!--
        @slot Nav Brands' quicklink on top of the search box -->
      <slot name="brandQuickLink">
        <a href="/brands/sephora-collection" class="menu-sub-title">
          Shop Sephora Collection
        </a>
      </slot>

      <form class="search-form floating-labels">
        <input
          id="search-brand"
          v-model="searchInput"
          type="text"
          class="ga-tracking-brand-search"
          autocomplete="off"
          placeholder="searchBoxLabel"
          @focus="enterBrandSearch"
        />
        <label
          for="search-brand"
          class="search-brand-label"
          v-html="searchBoxLabel"
        />
      </form>

      <nav class="letter-list">
        <template v-for="(brands, letter) in searchResult">
          <!--
            @slot Nav Brands' letter links on the left side
            @binding letter is intended as a lookup for brands starting with it
            @binding hasMatches indicates if any brands matching the letter  -->
          <slot
            name="letter-link"
            :hasMatches="brands.length > 0"
            :letter="letter"
          >
            <a
              :key="letter"
              class="letter-link"
              :class="{ disabled: brands.length == 0 }"
              tabindex="1"
              :href="`/brands#letter-${letter}`"
              @click.prevent="searchBrandsByLetter(letter)"
            >
              {{ letter }}
            </a>
          </slot>
        </template>
      </nav>
    </div>

    <section class="column brands-list" :style="{ '--padding': padding }">
      <section
        v-for="(brands, letter) in searchResult"
        :id="'brand-letter-' + letter"
        :key="letter"
        class="brands-sections"
        :class="{ 'is-hidden': brands.length == 0 }"
      >
        <!--
          @slot Nav Brands' letter links on the left side
          @binding letter as an indicator for brands starting on it -->
        <slot name="brand-menu-sub-title" :letter="letter">
          <div class="menu-sub-title">
            {{ letter }}
          </div>
        </slot>
        <ul>
          <li v-for="brand in brands" :key="brand['label']" class="menu-item">
            <!--
              @slot Nav Brands' letter links on the left side
              @binding brand is the detail of the brand. It has `label` and `link` -->
            <slot name="brand-link" :brand="brand">
              <a :href="brand.link" class="item-link" tabindex="0">
                {{ brand.label }}
              </a>
            </slot>
          </li>
        </ul>
      </section>
    </section>
  </div>
</template>

<script>
export default {
  name: 'NavBrands',
  props: {
    /** Object with brands split by starting letters */
    brandsByLetters: {
      type: Object,
      required: true
    },
    /** Label to be shown within the search box */
    searchBoxLabel: {
      type: String,
      required: true
    }
  },
  data: function () {
    return {
      searchInput: '',
      padding: '20'
    }
  },

  computed: {
    searchInputMatcher: function () {
      // escapes invalid character such as `\`, and `*` by adding `\` in front of it
      let input = this.searchInput.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1')
      input = this.normalizeAccentedBrandName(input)
      return RegExp(input, 'i')
    },
    searchResult: function () {
      let result = {}

      for (const letter in this.brandsByLetters) {
        result[letter] = this.brandsByLetters[letter].filter((brand) => {
          return this.searchInputMatcher.test(
            this.normalizeAccentedBrandName(brand['label'])
          )
        })
      }
      return result
    }
  },

  methods: {
    searchBrandsByLetter(letter) {
      const scrollTo = document.getElementById(`brand-letter-${letter}`)
      document.querySelector('.brands-list').scrollTop =
        scrollTo.offsetTop - this.padding
      this.$emit('searchByLetter', letter)
    },
    enterBrandSearch(object) {
      this.$emit('enterBrandSearch', object)
    },
    normalizeAccentedBrandName: function (brand) {
      return brand
        .normalize('NFD')
        .replace(/[\u0300-\u036F]/g, '')
        .toLowerCase()
    }
  }
}
</script>

<style lang="scss" scoped>
.nav-brands {
  height: 300px;
  width: 520px;
  margin: 0 auto;
  @include tablet {
    width: 355px;
  }
}

.menu-sub-title {
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;

  &:hover {
    color: $red;
    text-decoration: none;
  }
}

.brands-search {
  text-align: left;
}

.floating-labels {
  height: 40px;
  margin: 0 0 20px 0;
  position: relative;

  .search-brand-label {
    color: $grey-darker;
    font-size: 16px;
    left: 10px;
    position: absolute;
    top: 5px;
    transform: matrix(1, 0, 0, 1, 0, 7);
  }

  #search-brand {
    border: 1px solid $grey-lighter;
    font-size: 16px;
    padding: 19px 10px 5px 10px;
    width: 225px;
    @include tablet {
      width: 177px;
    }
  }

  #search-brand + .search-brand-label {
    transition: transform 0.15s, opacity 0.15s ease-in-out;
    transform-origin: 0 0;
  }

  #search-brand:not(:placeholder-shown) + .search-brand-label,
  #search-brand:focus + .search-brand-label {
    transform: scale(0.7);
  }

  #search-brand::placeholder {
    opacity: 0;
  }
}

.letter-list {
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.letter-link {
  // 7 letters per row
  width: calc(100% / 7);
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
  text-transform: uppercase;

  &.disabled,
  &.disabled:hover {
    color: $grey;
    pointer-events: none;
    text-decoration: none;
    /* For IE11/ MS Edge bug */
    display: inline-block;
  }

  &:hover {
    color: $red;
    text-decoration: none;
  }
}

.brands-list {
  border-left: 1px solid $grey;
  height: 300px;
  line-height: 20px;
  overflow-y: scroll;
  scroll-behavior: smooth;
  text-align: left;
  padding: var(--padding);

  & > * {
    padding-inline-start: 20px;
    padding: 0 0 0 20px;
  }

  .menu-sub-title:hover {
    color: $black;
    text-decoration: none;
  }

  .menu-sub-title:only-child {
    display: none;
  }

  .menu-item {
    line-height: 20px;
    margin: 0 0 3px 0;

    .item-link {
      color: $black;
      height: 24px;
      line-height: 24px;
    }

    .item-link:hover {
      color: $red;
      text-decoration: none;
    }
  }

  .menu-item:last-child {
    margin-bottom: 15px;
  }
}
</style>
